/**
 * Customer OIDC conflict / recovery codes (G9 / customer-not-registered-parity).
 *
 * Twin-parity (G2/G33): admin maps `OIDC_SUBJECT_IN_USE` on `OidcDonePage`;
 * customer maps the same wire codes here + AccountOidcLinkPanel. Shared patterns
 * only — see `admin-app/docs/OIDC_FE_ERROR_MESSAGE_TWIN_PARITY.md`.
 *
 * Phase B: CTA to Account & privacy Link only when authenticated; guests get
 * honest copy (no dead “open Link” affordance while signed out).
 */

import { CustomerAuthErrorCodes } from './authErrorCodes.js';

export function isOidcEmailConflictCode(code: string | null | undefined): boolean {
  if (code === null || code === undefined || code.length === 0) {
    return false;
  }
  return (
    code === CustomerAuthErrorCodes.OIDC_EMAIL_CONFLICT_QUERY ||
    code === CustomerAuthErrorCodes.OIDC_EMAIL_CONFLICT
  );
}

export function isOidcSubjectInUseCode(code: string | null | undefined): boolean {
  if (code === null || code === undefined || code.length === 0) {
    return false;
  }
  return (
    code === CustomerAuthErrorCodes.OIDC_SUBJECT_IN_USE ||
    code === CustomerAuthErrorCodes.OIDC_SUBJECT_IN_USE_QUERY
  );
}

/** Email-not-mergeable or IdP subject already bound elsewhere — Account Link recovery. */
export function isOidcLinkConflictRecoveryCode(
  code: string | null | undefined,
): boolean {
  return isOidcEmailConflictCode(code) || isOidcSubjectInUseCode(code);
}

/**
 * i18n key for conflict body. Guests: honest “sign in first…” copy.
 * Authenticated: copy that points at Account & privacy Link (CTA optional).
 */
export function resolveOidcConflictMessageKey(
  code: string | null | undefined,
  authenticated: boolean,
): string | undefined {
  if (isOidcEmailConflictCode(code)) {
    return authenticated
      ? 'auth.oidc.error.emailConflictAuthenticated'
      : 'auth.oidc.error.emailConflict';
  }
  if (isOidcSubjectInUseCode(code)) {
    return authenticated
      ? 'auth.oidc.error.subjectInUseAuthenticated'
      : 'auth.oidc.error.subjectInUse';
  }
  return undefined;
}

/** Heuristic when BE still throws field=`oidc` without a stable SUBJECT_IN_USE code. */
export function looksLikeSubjectInUseMessage(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('already linked to another customer') ||
    normalized.includes('již propojena s jiným zákaznickým') ||
    normalized.includes('už prepojená s iným zákazníckym')
  );
}

export function buildAccountOidcPrivacyPath(tenantCode: string): string {
  void tenantCode;
  return '/account/account-and-privacy#credentials';
}

/**
 * Signed-in guest-route bounce: keep conflict `error` so AccountOidcLinkPanel
 * can show recovery (RequireGuestAuth otherwise drops query on default redirect).
 */
export function buildAuthenticatedOidcConflictRedirect(
  tenantCode: string,
  errorCode: string,
): string {
  void tenantCode;
  return `/account/account-and-privacy?error=${encodeURIComponent(errorCode)}#credentials`;
}
